/**
 * /policies/eos-rate-matrix/autoflush — M3X.EOS F.21 KEYSTONE page.
 *
 * The "agentic re-frame" page: HR clicks "Run demo extraction"
 * (or in v2, uploads a customer HR manual PDF) → sees the per-cell
 * Mutaba'aa floor enforcement + auto-commit / pending-review /
 * vetoed breakdown → reviews + approves → ~25 min total HR time.
 *
 * Mirrors loans F.21 + leaves F.20 design discipline.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PageHeader } from "../components/ui";
import { api, type EosAutoFlushResultDTO } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

export function EosOnboardingAutoflushPage() {
  const { t } = useTranslation();
  const legalEntityId = useLegalEntityId();
  const [result, setResult] = useState<EosAutoFlushResultDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function runDemo() {
    if (!legalEntityId) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await api.runEosOnboardingDemo({
        legal_entity_id: legalEntityId,
      });
      setResult(r);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "eos_autoflush.title",
          "EOS Onboarding Auto-flush",
        )}
        subtitle={t(
          "eos_autoflush.subtitle",
          "The agentic re-frame: Sonnet extracts from customer HR manual; Mutaba'aa validates against statutory floors; HR reviews flagged cells. Target: ~25 min HR time.",
        )}
        actions={
          <Link
            to="/policies/eos-rate-matrix"
            className="rounded-md border border-brand-blue px-3 py-1.5 text-sm text-brand-blue hover:bg-brand-blue/5"
          >
            {t("eos_autoflush.view_matrix", "View rate matrix")}
          </Link>
        }
      />

      <div className="rounded-lg border border-brand-blue/30 bg-brand-blue/5 p-5">
        <h3 className="text-base font-semibold text-brand-deep">
          {t(
            "eos_autoflush.demo_heading",
            "Run with demo extraction",
          )}
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          {t(
            "eos_autoflush.demo_body",
            "Runs a curated set of demo proposed cells (what Sonnet would extract from a sample Saudi customer HR manual) through Mutaba'aa. You'll see auto-committed cells (high confidence + above floor), pending-review cells (HR judgment), and vetoed cells (below statutory floor).",
          )}
        </p>
        <button
          type="button"
          onClick={runDemo}
          disabled={loading || !legalEntityId}
          className="mt-3 rounded-md bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
        >
          {loading
            ? t("eos_autoflush.running", "Running autoflush…")
            : t("eos_autoflush.run_demo", "✦ Run demo extraction")}
        </button>
        {err && (
          <p className="mt-3 rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            {err}
          </p>
        )}
      </div>

      {result && (
        <>
          <div className="grid gap-4 sm:grid-cols-4">
            <SummaryCard
              label={t("eos_autoflush.proposed", "Proposed")}
              count={result.total_proposed}
              tone="neutral"
            />
            <SummaryCard
              label={t("eos_autoflush.auto_committed", "Auto-committed")}
              count={result.auto_committed_count}
              tone="emerald"
            />
            <SummaryCard
              label={t("eos_autoflush.pending_review", "Pending HR review")}
              count={result.pending_review_count}
              tone="amber"
            />
            <SummaryCard
              label={t("eos_autoflush.vetoed", "Mutaba'aa vetoed")}
              count={result.vetoed_count}
              tone="rose"
            />
          </div>

          <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
            <h3 className="mb-3 text-sm font-semibold text-text-primary">
              {t(
                "eos_autoflush.per_cell_results",
                "Per-cell results — Mutaba'aa decision",
              )}
            </h3>
            <table className="w-full text-xs">
              <thead className="text-text-tertiary">
                <tr className="border-b border-slate-200">
                  <th className="py-2 text-start">
                    {t("eos_autoflush.trigger", "Trigger")}
                  </th>
                  <th className="py-2 text-start">
                    {t("eos_autoflush.tier", "Tier")}
                  </th>
                  <th className="py-2 text-end">
                    {t("eos_autoflush.proposed_pct", "Proposed%")}
                  </th>
                  <th className="py-2 text-end">
                    {t("eos_autoflush.floor_pct", "Floor%")}
                  </th>
                  <th className="py-2 text-end">
                    {t("eos_autoflush.confidence", "Conf.")}
                  </th>
                  <th className="py-2 text-start ps-3">
                    {t("eos_autoflush.status", "Status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.per_cell.map((c, idx) => (
                  <tr
                    key={idx}
                    className={`border-b border-slate-100 ${
                      c.status === "mutabaa_vetoed"
                        ? "bg-rose-50"
                        : c.status === "pending_hr_review"
                          ? "bg-amber-50"
                          : "bg-emerald-50"
                    }`}
                  >
                    <td className="py-2 font-mono text-[10px]">
                      {c.trigger_type}
                    </td>
                    <td className="py-2">{c.tier_label}</td>
                    <td className="py-2 text-end font-mono">{c.percent}%</td>
                    <td className="py-2 text-end font-mono text-text-tertiary">
                      {c.statutory_floor_percent}%
                    </td>
                    <td className="py-2 text-end">
                      {Math.round(Number(c.extractor_confidence) * 100)}%
                    </td>
                    <td className="py-2 ps-3">
                      <div className="flex flex-col">
                        <StatusBadge status={c.status} />
                        {c.veto_reason && (
                          <span className="mt-1 text-[10px] text-rose-700">
                            {c.veto_reason}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-slate-200 bg-surface-subtle p-4 text-xs text-text-secondary">
            <p>
              {t(
                "eos_autoflush.help",
                "Auto-committed cells are written to the rate matrix immediately. Pending-review cells need HR to accept/reject (manual step). Vetoed cells cannot be committed in any mode (Article 84/85 statutory floor).",
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: "neutral" | "emerald" | "amber" | "rose";
}) {
  const cls =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "amber"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : tone === "rose"
          ? "border-rose-200 bg-rose-50 text-rose-900"
          : "border-slate-200 bg-surface-default text-text-primary";
  return (
    <div className={`rounded-lg border p-4 ${cls}`}>
      <div className="text-xs uppercase tracking-wider">{label}</div>
      <div className="mt-1 font-mono text-3xl font-bold">{count}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  if (status === "auto_committed") {
    return (
      <span className="rounded-full bg-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-900">
        {t("eos_autoflush.status_auto", "✓ Auto-committed")}
      </span>
    );
  }
  if (status === "mutabaa_vetoed") {
    return (
      <span className="rounded-full bg-rose-200 px-2 py-0.5 text-[10px] font-bold text-rose-900">
        {t("eos_autoflush.status_vetoed", "✦ Mutaba'aa vetoed")}
      </span>
    );
  }
  return (
    <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-900">
      {t("eos_autoflush.status_pending", "✦ Pending HR review")}
    </span>
  );
}

export default EosOnboardingAutoflushPage;
